import { ensureWebMCP } from './webmcp/polyfill'

ensureWebMCP()

import './webmcp'

import './fonts.css'
import './tokens.css'
import './style.css'
import './workspace.css'
import './marketing.css'
import { buildMeasureTask } from './demo/measures'
import * as store from './store/taskStore'
import { deleteTask } from './persistence/taskRepository'
import { mount } from './ui/bench'
import { currentTaskIdFromLocation } from './webmcp/location'
import { initializeCloud } from './cloud'

const root = document.querySelector<HTMLElement>('#app')
if (!root) throw new Error('#app introuvable')

const lié = currentTaskIdFromLocation()

mount(root)
void initializeCloud()

if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined)
  })
}

/**
 * L'adresse décide du cahier.
 *
 * `/t/:id` ouvre CE cahier. Sans identifiant, `/` reste la vitrine publique,
 * même si ce navigateur contient déjà une mémoire. La différence est décisive
 * dès qu'il y a plus d'une tâche sur l'appareil : liée, la page rend la tâche
 * nommée ou dit qu'elle a disparu ; publique, elle n'ouvre rien implicitement.
 *
 * `?mesure=N` charge la tâche de mesure N au chargement de la page.
 *
 * Le protocole du J6 doit être rejouable par une simple URL : un juge ouvre
 * l'adresse et retrouve exactement l'état sur lequel la mesure a été faite,
 * sans manipulation. La tâche n'est reconstruite que si le cahier ouvert n'est
 * pas déjà celle-là, pour qu'un rechargement en cours d'essai ne remette pas
 * le compteur à zéro.
 *
 * IMPORTANT — `/` ne fabrique et n'ouvre JAMAIS de tâche implicitement.
 * Un premier visiteur (donc tout juge du concours) doit voir la vitrine
 * publique, pas un espace de travail créé à sa place : c'est elle qui explique
 * le produit et propose « Create a task ». `initPublicLanding()` charge juste
 * la liste des cahiers déjà présents pour que `renderReturningVisitor()`
 * puisse offrir un retour — sans jamais s'y lier.
 */
void (async () => {
  // Purge any legacy demo task from local IndexedDB
  try {
    const all = await store.allTasks()
    for (const t of all) {
      if (t.id === 'nightorder-demo' || t.id === 'keydler-demo') {
        await deleteTask(t.id)
      }
    }
  } catch {
    // Ignore early storage read error
  }

  const isDemoUrl = lié === 'nightorder-demo' || lié === 'keydler-demo'

  if (lié && !isDemoUrl) {
    await store.init(lié)
  } else if (isDemoUrl) {
    // Anciens liens de démo (`/t/nightorder-demo`, `/t/keydler-demo`) : on les
    // fait retomber sur un vrai cahier existant, ou on en ouvre un de secours.
    // Ce comportement est conservé tel quel — seule la racine `/` change.
    const all = await store.allTasks()
    const real = all.filter(
      (t) => t.id !== 'nightorder-demo' && t.id !== 'keydler-demo' && !t.id.startsWith('mesure-'),
    )
    if (real.length > 0) {
      await store.init(real[0].id)
      history.replaceState(null, '', `/t/${real[0].id}${location.hash || '#memory-detail'}`)
    } else {
      const prodTask = await store.createAndOpenTask(
        'Production Workspace',
        'Ready for AI agent instructions via WebMCP',
      )
      history.replaceState(null, '', `/t/${prodTask.id}${location.hash || '#memory-detail'}`)
    }
  } else {
    // `/` nue : vitrine publique. Aucune tâche créée, aucune ouverte.
    await store.initPublicLanding()
  }

  const n = Number(new URLSearchParams(location.search).get('mesure'))
  if (!n) return

  const voulue = buildMeasureTask(n)
  if (store.currentTask()?.title === voulue.title) return
  await store.openPreparedTask(voulue)
})()
